import ThemedSelect from '../../common/ThemedSelect';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Upload, FileText, Trash2 } from 'lucide-react';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { addYears } from 'date-fns';
import { toApiDateString } from '../../../utils/dateUtils';
import api from '../../../services/weighbridgeComplianceApi';

const initialForm = {
  assetId: '',
  certificateType: 'Calibration',
  certificateNumber: '',
  issueDate: null,
  expiryDate: null,
  verificationOfficerName: '',
  officerNrcsNumber: '',
  verificationLabName: '',
  labSanasAccreditationNumber: '',
  result: 'Valid',
  sealNumbers: '',
  ambientTemperature: '',
  findings: '',
  accreditationNumber: '',
  sealNumber: '',
  requiresReVerification: false,
};

const requiredFields = [
  'assetId',
  'certificateType',
  'certificateNumber',
  'issueDate',
  'expiryDate',
  'verificationOfficerName',
  'verificationLabName',
  'labSanasAccreditationNumber',
  'result',
];

const CalibrationModal = ({ calibration, assets, onClose, onSaved }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (calibration) {
      setForm({
        assetId: calibration.asset_id || '',
        certificateType: calibration.calibration_type || 'Calibration',
        certificateNumber: calibration.certificate_number || '',
        issueDate: calibration.issue_date ? new Date(calibration.issue_date) : null,
        expiryDate: calibration.next_due_date ? new Date(calibration.next_due_date) : null,
        verificationOfficerName: calibration.verification_officer || '',
        officerNrcsNumber: calibration.verification_officer_nrcs_number || '',
        verificationLabName: calibration.calibrating_entity || '',
        labSanasAccreditationNumber: calibration.calibrating_entity_sanas_number || '',
        result: calibration.status || 'Valid',
        sealNumbers: calibration.seal_numbers || '',
        ambientTemperature: calibration.ambient_temperature || '',
        findings: calibration.findings || '',
        // TODO: accreditation_number is a verification field, not a calibrations column
        accreditationNumber: calibration.accreditation_number || '',
        // TODO: seal_number is a verification field, not a calibrations column
        sealNumber: calibration.seal_number || '',
        // TODO: requires_re_verification is a verification field, not a calibrations column
        requiresReVerification: calibration.requires_re_verification || false,
      });
    } else {
      setForm(initialForm);
      setFiles([]);
    }
    setErrors({});
  }, [calibration]);

  const handleIssueDateChange = useCallback(
    (date) => {
      setForm((prev) => {
        const newForm = { ...prev, issueDate: date };
        if (date && !calibration?.next_due_date) {
          newForm.expiryDate = addYears(date, 2);
        }
        return newForm;
      });
    },
    [calibration]
  );

  const validate = () => {
    const newErrors = {};
    requiredFields.forEach((field) => {
      const value = form[field];
      if (value === '' || value === null || value === undefined) {
        const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
        newErrors[field] = `${label} is required`;
      }
    });
    if (form.issueDate && form.expiryDate && form.expiryDate <= form.issueDate) {
      newErrors.expiryDate = 'Expiry date must be after issue date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        asset_id: form.assetId ? Number(form.assetId) : undefined,
        calibration_type: form.certificateType,
        certificate_number: form.certificateNumber,
        issue_date: form.issueDate ? toApiDateString(form.issueDate) : undefined,
        next_due_date: form.expiryDate ? toApiDateString(form.expiryDate) : undefined,
        verification_officer: form.verificationOfficerName,
        verification_officer_nrcs_number: form.officerNrcsNumber,
        calibrating_entity: form.verificationLabName,
        calibrating_entity_sanas_number: form.labSanasAccreditationNumber,
        status: form.result,
        seal_numbers: form.sealNumbers,
        ambient_temperature: form.ambientTemperature ? Number(form.ambientTemperature) : undefined,
        findings: form.findings,
      };

      let savedId;
      if (calibration?.id) {
        await api.calibrations.update(calibration.id, payload);
        savedId = calibration.id;
        toast.success('Certificate updated');
      } else {
        const res = await api.calibrations.create(payload);
        savedId = res.data?.id || res.data?.data?.id;
        toast.success('Certificate created');
      }

      if (files.length > 0 && savedId) {
        setUploading(true);
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('linked_entity_type', 'Calibration');
          formData.append('linked_entity_id', savedId);
          formData.append('description', `Certificate document: ${file.name}`);
          try {
            await api.documents.upload(formData);
          } catch (err) {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        setUploading(false);
      }

      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === 'application/pdf' || f.name.endsWith('.pdf')
    );
    if (droppedFiles.length > 0) {
      setFiles((prev) => [...prev, ...droppedFiles]);
    } else {
      toast.error('Only PDF files are accepted');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const isVerificationType =
    form.certificateType === 'Verification' || form.certificateType === 'Both';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2
            className="text-lg font-bold text-slate-800"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {calibration?.id ? 'Edit Certificate' : 'Add Certificate'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Asset *
              </label>
              <ThemedSelect
                value={form.assetId}
                onChange={(e) => setForm({ ...form, assetId: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.assetId ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="">Select asset...</option>
                {(assets || []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </ThemedSelect>
              {errors.assetId && <p className="text-xs text-red-500 mt-1">{errors.assetId}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Certificate Type *
              </label>
              <ThemedSelect
                value={form.certificateType}
                onChange={(e) => setForm({ ...form, certificateType: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.certificateType ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="Calibration">Calibration</option>
                <option value="Verification">Verification</option>
                <option value="Both">Both</option>
              </ThemedSelect>
              {errors.certificateType && (
                <p className="text-xs text-red-500 mt-1">{errors.certificateType}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Certificate Number *
              </label>
              <input
                type="text"
                value={form.certificateNumber}
                onChange={(e) => setForm({ ...form, certificateNumber: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.certificateNumber ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="e.g. CAL-2024-001"
              />
              {errors.certificateNumber && (
                <p className="text-xs text-red-500 mt-1">{errors.certificateNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Result *
              </label>
              <ThemedSelect
                value={form.result}
                onChange={(e) => setForm({ ...form, result: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.result ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              >
                <option value="Valid">Pass (Valid)</option>
                <option value="Rejected">Fail (Rejected)</option>
                <option value="Pending">Pending</option>
                <option value="Expired">Expired</option>
              </ThemedSelect>
              {errors.result && <p className="text-xs text-red-500 mt-1">{errors.result}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Issue Date *
              </label>
              <DatePicker
                selected={form.issueDate}
                onChange={handleIssueDateChange}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select issue date"
                className={`w-full px-3 py-2.5 border ${errors.issueDate ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              />
              {errors.issueDate && <p className="text-xs text-red-500 mt-1">{errors.issueDate}</p>}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Expiry Date *
              </label>
              <DatePicker
                selected={form.expiryDate}
                onChange={(date) => setForm({ ...form, expiryDate: date })}
                dateFormat="yyyy-MM-dd"
                placeholderText="Auto: issue date + 2 years"
                className={`w-full px-3 py-2.5 border ${errors.expiryDate ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
              />
              {errors.expiryDate && (
                <p className="text-xs text-red-500 mt-1">{errors.expiryDate}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Verification Officer Name *
              </label>
              <input
                type="text"
                value={form.verificationOfficerName}
                onChange={(e) => setForm({ ...form, verificationOfficerName: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.verificationOfficerName ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="Officer name"
              />
              {errors.verificationOfficerName && (
                <p className="text-xs text-red-500 mt-1">{errors.verificationOfficerName}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Officer NRCS Number
              </label>
              <input
                type="text"
                value={form.officerNrcsNumber}
                onChange={(e) => setForm({ ...form, officerNrcsNumber: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="NRCS registration number"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Verification Lab Name *
              </label>
              <input
                type="text"
                value={form.verificationLabName}
                onChange={(e) => setForm({ ...form, verificationLabName: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.verificationLabName ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="Lab name"
              />
              {errors.verificationLabName && (
                <p className="text-xs text-red-500 mt-1">{errors.verificationLabName}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Lab SANAS Accreditation No *
              </label>
              <input
                type="text"
                value={form.labSanasAccreditationNumber}
                onChange={(e) => setForm({ ...form, labSanasAccreditationNumber: e.target.value })}
                className={`w-full px-3 py-2.5 border ${errors.labSanasAccreditationNumber ? 'border-red-300' : 'border-slate-200'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm`}
                placeholder="e.g. SANAS-T0123"
              />
              {errors.labSanasAccreditationNumber && (
                <p className="text-xs text-red-500 mt-1">{errors.labSanasAccreditationNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Seal Numbers
              </label>
              <input
                type="text"
                value={form.sealNumbers}
                onChange={(e) => setForm({ ...form, sealNumbers: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="Comma-separated seal numbers"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                Ambient Temperature (C)
              </label>
              <input
                type="number"
                value={form.ambientTemperature}
                onChange={(e) => setForm({ ...form, ambientTemperature: e.target.value })}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                placeholder="e.g. 22"
              />
            </div>
          </div>

          {isVerificationType && (
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                Verification Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Accreditation Number
                  </label>
                  <input
                    type="text"
                    value={form.accreditationNumber}
                    onChange={(e) => setForm({ ...form, accreditationNumber: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Accreditation number"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Seal Number
                  </label>
                  <input
                    type="text"
                    value={form.sealNumber}
                    onChange={(e) => setForm({ ...form, sealNumber: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                    placeholder="Seal number"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.requiresReVerification}
                      onChange={(e) =>
                        setForm({ ...form, requiresReVerification: e.target.checked })
                      }
                      className="w-4 h-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                    />
                    <span className="text-sm text-slate-700">Requires Re-Verification</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Findings / Notes
            </label>
            <textarea
              value={form.findings}
              onChange={(e) => setForm({ ...form, findings: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
              placeholder="Any findings, observations, or notes..."
            />
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Document Upload
            </h3>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                dragActive
                  ? 'border-teal-400 bg-teal-50/50'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600 mb-1">Drag and drop PDF files here, or</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm font-medium text-teal-600 hover:text-teal-700 underline"
              >
                click to browse
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-slate-400 mt-2">PDF files only</p>
            </div>

            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2"
                  >
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-700 flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-slate-400">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 text-sm text-slate-600"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || uploading}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-semibold disabled:opacity-50"
          >
            {saving
              ? 'Saving...'
              : uploading
                ? 'Uploading...'
                : calibration?.id
                  ? 'Update'
                  : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalibrationModal;
